import React, { useEffect, useState } from "react";
import {
  Label,
  Input,
  Button,
  Field,
  Textarea,
  Select,
  Dropdown,
  Option,
  Switch,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { Add20Filled, DeleteRegular } from "@fluentui/react-icons";
import "../CSS/WorkflowForm.css";
import InfoButton from "../GenericComponent/InfoButton";

const WorkflowForm = (props) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [mappingPairs, setMappingPairs] = useState([{ key: "", value: "" }]);
  const [dataQueryHeaderPairs, setDataQueryHeaderPairs] = useState([{ key: "", value: "" }]);
  const [bodyText, setBodyText] = useState("");
  const [jsonError, setJsonError] = useState(false);
  const [showBody, setShowBody] = useState(true);
  const [showMapping, setShowMapping] = useState(false);

  const methodOptions = ["GET", "POST", "PUT"];
  const triggerOptions = [
    "AGENT_CONNECTED",
    "CUSTOMER_ABANDONED",
    "CALL_CONNECTED",
    "CALL_ENDED",
    "AGENT_DECLINED",
  ];

  useEffect(() => {
    if (props.workflow) {
      validateForm();

      // Charger les données en mode update
      if (props.isUpdate) {
        // Mapping
        if (props.workflow.action?.mapping && Object.keys(props.workflow.action.mapping).length > 0) {
          const pairs = Object.entries(props.workflow.action.mapping).map(([k, v]) => ({
            key: k,
            value: String(v),
          }));
          setMappingPairs(pairs);
        }

        // Body
        if (props.workflow.action?.body && Object.keys(props.workflow.action.body).length > 0) {
          setBodyText(JSON.stringify(props.workflow.action.body, null, 2));
        }

        // Headers
        if (props.workflow.action?.headers && Object.keys(props.workflow.action.headers).length > 0) {
          const pairs = Object.entries(props.workflow.action.headers).map(([k, v]) => ({
            key: k,
            value: String(v),
          }));
          setDataQueryHeaderPairs(pairs);
        }
      }
    }
  }, [props.workflow, props.isUpdate]);
console.log(props.workflow ,"workflow props");

  // Toggles selon la méthode HTTP, MAIS sans masquer des données déjà présentes.
  // (Bug précédent : POST forçait showMapping=false → un workflow POST avec un
  //  `action.mapping` existant voyait sa section mapping cachée "off" à chaque chargement.)
  // Deps = [method] uniquement → ne se redéclenche pas sur édition → ne combat pas l'utilisateur.
  useEffect(() => {
    const action = props.workflow?.action || {};
    const method = action.method;
    const hasMapping = Object.keys(action.mapping || {}).length > 0;
    const hasBody = Object.keys(action.body || {}).length > 0;
    if (method === 'GET') {
      setShowMapping(true);
      setShowBody(hasBody);
    } else if (method === 'POST') {
      setShowMapping(hasMapping);            // visible si un mapping existe déjà
      setShowBody(hasBody || !hasMapping);   // défaut body si pas de mapping
    } else if (method === 'PUT') {
      setShowMapping(true);
      setShowBody(true);
    }
    // eslint-disable-next-line
  }, [props.workflow?.action?.method]);

  // Helper function to validate URL
  const isValidUrl = (urlString) => {
    if (!urlString || !urlString.trim()) return true; // Empty is ok, not required
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (e) {
      return false;
    }
  };

  // Helper function to validate JSON with strict rules
  const validateJSON = (jsonString) => {
    if (!jsonString || !jsonString.trim()) return { valid: true };

    try {
      // Basic JSON parse
      const parsed = JSON.parse(jsonString);

      // Check for duplicate keys by comparing original string
      const jsonText = jsonString.trim();

      // Detect duplicate keys using regex
      const keyRegex = /"([^"]+)"\s*:/g;
      const keys = [];
      let match;

      while ((match = keyRegex.exec(jsonText)) !== null) {
        keys.push(match[1]);
      }

      // Check for duplicates
      const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
      if (duplicates.length > 0) {
        return {
          valid: false,
          error: "DUPLICATE_JSON_KEYS",
          duplicateKeys: [...new Set(duplicates)].join(", ")
        };
      }

      // Verify it's an object, not an array or primitive
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return {
          valid: false,
          error: "JSON_MUST_BE_OBJECT"
        };
      }

      return { valid: true, parsed };
    } catch (e) {
      return {
        valid: false,
        error: "INVALID_JSON"
      };
    }
  };

  const validateForm = () => {
    if (!props.workflow) return;

    const newErrors = {};

    // Validation champs obligatoires
    if (!props.workflow.displayName)
      newErrors.displayName = t("THIS_FIELD_IS_REQUIRED");
    if (!props.workflow.triggerEvent)
      newErrors.triggerEvent = t("THIS_FIELD_IS_REQUIRED");
    if (!props.workflow.scope) newErrors.scope = t("THIS_FIELD_IS_REQUIRED");

    // Validation URLs
    if (props.workflow.action?.url && !isValidUrl(props.workflow.action.url)) {
      newErrors.actionUrl = t("INVALID_URL");
    }

    // Validation mapping: vérifier que les clés non vides ont des valeurs
    const nonEmptyMappings = mappingPairs.filter((p) => p.key.trim());
    for (const pair of nonEmptyMappings) {
      if (!pair.value.trim()) {
        newErrors.mapping = t("MAPPING_VALUES_REQUIRED");
        break;
      }
    }

    // Validation DataQuery Headers: vérifier que les clés non vides ont des valeurs
    const nonEmptyDataQueryHeaders = dataQueryHeaderPairs.filter((p) => p.key.trim());
    for (const pair of nonEmptyDataQueryHeaders) {
      if (!pair.value.trim()) {
        newErrors.dataQueryHeaders = t("HEADER_VALUES_REQUIRED");
        break;
      }
    }

    // Validation JSON body
    if (bodyText.trim()) {
      const jsonValidation = validateJSON(bodyText);
      if (!jsonValidation.valid) {
        if (jsonValidation.duplicateKeys) {
          newErrors.body = `${t(jsonValidation.error)}: ${jsonValidation.duplicateKeys}`;
        } else {
          newErrors.body = t(jsonValidation.error);
        }
      }
    }

    setErrors(newErrors);

    const isValid = Object.keys(newErrors).length === 0;
    if (props.setFormValid) props.setFormValid(isValid);
  };

  useEffect(() => {
    validateForm();
  }, [mappingPairs, dataQueryHeaderPairs, bodyText, props.workflow?.displayName, props.workflow?.triggerEvent, props.workflow?.scope, props.workflow?.action?.url]);

  // Mapping handlers
  const handleMappingChange = (index, field, value) => {
    const updated = [...mappingPairs];
    updated[index][field] = value;
    setMappingPairs(updated);

    const mappingObj = {};
    updated.forEach((pair) => {
      if (pair.key.trim()) {
        mappingObj[pair.key.trim()] = pair.value; // Doit rester string
      }
    });

    props.setWorkflow((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        mapping: mappingObj,
      },
    }));
  };

  const addMappingPair = () => {
    setMappingPairs([...mappingPairs, { key: "", value: "" }]);
  };

  const removeMappingPair = (index) => {
    const updated = mappingPairs.filter((_, i) => i !== index);
    setMappingPairs(updated.length > 0 ? updated : [{ key: "", value: "" }]);

    // Mettre à jour le workflow
    const mappingObj = {};
    updated.forEach((pair) => {
      if (pair.key.trim()) {
        mappingObj[pair.key.trim()] = pair.value;
      }
    });

    props.setWorkflow((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        mapping: mappingObj,
      },
    }));
  };

  // DataQuery Headers handlers
  const handleDataQueryHeaderChange = (index, field, value) => {
    const updated = [...dataQueryHeaderPairs];
    updated[index][field] = value;
    setDataQueryHeaderPairs(updated);

    // Convert to object and update workflow
    const headersObj = {};
    updated.forEach((pair) => {
      if (pair.key.trim()) {
        headersObj[pair.key] = pair.value;
      }
    });

    props.setWorkflow((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        headers: headersObj,
      },
    }));
  };

  const addDataQueryHeaderPair = () => {
    setDataQueryHeaderPairs([...dataQueryHeaderPairs, { key: "", value: "" }]);
  };

  const removeDataQueryHeaderPair = (index) => {
    const updated = dataQueryHeaderPairs.filter((_, i) => i !== index);
    setDataQueryHeaderPairs(updated.length > 0 ? updated : [{ key: "", value: "" }]);

    // Mettre à jour le workflow
    const headersObj = {};
    updated.forEach((pair) => {
      if (pair.key.trim()) {
        headersObj[pair.key] = pair.value;
      }
    });

    props.setWorkflow((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        headers: headersObj,
      },
    }));
  };

 

  // Body handler
  const handleBodyChange = (value) => {
    setBodyText(value);
    setJsonError(false);

    if (value.trim()) {
      const jsonValidation = validateJSON(value);
      if (jsonValidation.valid && jsonValidation.parsed) {
        props.setWorkflow((prev) => ({
          ...prev,
          action: {
            ...prev.action,
            body: jsonValidation.parsed,
          },
        }));
      } else {
        setJsonError(true);
      }
    } else {
      props.setWorkflow((prev) => ({
        ...prev,
        action: {
          ...prev.action,
          body: {},
        },
      }));
    }
  };



  return (
    <div className="workflow-form-container">
      <div className="form-section-workflow">
        <Label required className="main-label">{t("DISPLAY_NAME")}</Label>
        <Field
          required
          validationMessage={errors.displayName}
          validationState={errors.displayName ? "error" : undefined}
        >
          <Input
            className="workflow-form"
            placeholder={t("ENTER_DISPLAY_NAME")}
            value={props.workflow?.displayName || ""}
            onChange={(e) =>
              props.setWorkflow((prev) => ({
                ...prev,
                displayName: e.target.value,
              }))
            }
          />
        </Field>

        <Label required className="main-label">{t("SCOPE")}</Label>
        <Field
          required
          validationMessage={errors.scope}
          validationState={errors.scope ? "error" : undefined}
        >
          <Dropdown
             className="workflow-form"
            placeholder={t("SELECT_ENTITY")}
            value={props.workflow?.scope || ""}
            selectedOptions={props.workflow?.scope ? [props.workflow.scope] : []}
            onOptionSelect={(e, data) =>
              props.setWorkflow((prev) => ({
                ...prev,
                scope: data.optionValue,
              }))
            }
          >
            {props.SCOPES_NAMES?.map((scope) => (
              <Option key={scope} value={scope}>
                {scope}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Label required className="main-label">{t("TRIGGER_EVENT")}</Label>
        <Field
          required
          validationMessage={errors.triggerEvent}
          validationState={errors.triggerEvent ? "error" : undefined}
        >
          <Dropdown
             className="workflow-form"
            placeholder={t("SELECT_TRIGGER")}
            value={props.workflow?.triggerEvent ? props.workflow.triggerEvent.toLowerCase().replace(/_/g, ' ') : ""}
            selectedOptions={props.workflow?.triggerEvent ? [props.workflow.triggerEvent] : []}
            onOptionSelect={(e, data) =>
              props.setWorkflow((prev) => ({
                ...prev,
                triggerEvent: data.optionValue,
              }))
            }
          >
            {triggerOptions.map((trigger) => (
              <Option key={trigger} value={trigger}>
                {trigger.toLowerCase().replace(/_/g, ' ')}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      {/* DATA QUERY SECTION */}
      <div className="form-section">
        <div className="section-header-workflow">
          <Label className="main-label">{t("ACTION")}</Label>
          <InfoButton infoKey="ACTION" category="WORKFLOWS" />
        </div>
        <Field >
          <Dropdown
             className="workflow-form"
            value={props.workflow?.action?.method || "POST"}
            selectedOptions={[props.workflow?.action?.method || "POST"]}
            onOptionSelect={(e, data) =>
              props.setWorkflow((prev) => ({
                ...prev,
                action: {
                  ...prev.action,
                  method: data.optionValue,
                },
              }))
            }
          >
            {methodOptions.map((method) => (
              <Option key={method} value={method}>
                {method}
              </Option>
            ))}
          </Dropdown>

        </Field>
        <Field validationMessage={errors.actionUrl} validationState={errors.actionUrl ? "error" : undefined}>
          <Input
             className="workflow-form"
            value={props.workflow?.action?.url || ""}
            onChange={(e) =>
              props.setWorkflow((prev) => ({
                ...prev,
                action: {
                  ...prev.action,
                  url: e.target.value,
                },
              }))
            }
            placeholder="https://api.example.com/endpoint"
          />
        </Field>

        <Label className="main-label" style={{marginLeft: "5rem"}}>{t("HEADERS")}</Label>

        <div className="key-value-pairs" style={{marginLeft: "5rem"}}>
          {dataQueryHeaderPairs.map((pair, index) => (
            <div key={index} className="pair-row">
             <Input
         
            resize="vertical"
                placeholder={t("KEY")}
                value={pair.key}
                onChange={(e) => handleDataQueryHeaderChange(index, "key", e.target.value)}
                className="pair-key"
              />
              <Input
       
            resize="vertical"
                placeholder={t("VALUE")}
                value={pair.value}
                onChange={(e) => handleDataQueryHeaderChange(index, "value", e.target.value)}
                className="pair-value"
              />
              {dataQueryHeaderPairs.length > 1 && (
                <Button
                  icon={<DeleteRegular />}
                  appearance="subtle"
                  onClick={() => removeDataQueryHeaderPair(index)}
                />
              )}
            </div>
          ))}
        </div>
        {errors.dataQueryHeaders && (
          <div className="validation-error">
            {errors.dataQueryHeaders}
          </div>
        )}
        <Button
          className="workflow-form"
          icon={<Add20Filled />}
          appearance="primary"
          onClick={addDataQueryHeaderPair}
        >
        
        </Button>
      </div>

      

      
      {/* BODY SECTION - Toggle avec défaut intelligent */}
      <div className="form-section" style={{marginTop:"1rem"}}>
        <div style={{display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", marginLeft: "5rem"}}>
          <Label className="main-label" style={{marginBottom: 0}}>{t("BODY_JSON")}</Label>
          <Switch
            checked={showBody}
            onChange={(e, data) => setShowBody(data.checked)}
          />
        </div>

        {showBody && (
          <Field
            validationMessage={jsonError ? errors.body || t("INVALID_JSON") : undefined}
            validationState={jsonError ? "error" : undefined}
          >
            <Textarea
             className="workflow-form"
              rows={8}
              value={bodyText}
              onChange={(e) => handleBodyChange(e.target.value)}
              placeholder='{"key": "value"}'
             
              resize="vertical"
            />
          </Field>
        )}
      </div>

       {/* MAPPING SECTION - Toggle avec défaut intelligent */}
      <div className="form-section">
        <div style={{display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", marginLeft: "5rem"}}>
          <Label className="main-label" style={{marginBottom: 0}}>{t("MAPPING")}</Label>
          <Switch
            checked={showMapping}
            onChange={(e, data) => setShowMapping(data.checked)}
          />
        </div>

        {showMapping && (
          <>
            <div className="key-value-pairs" style={{marginLeft: "5rem"}}>
              {mappingPairs.map((pair, index) => (
                <div key={index} className="pair-row">
                  <Input
                    placeholder={t("KEY")}
                    value={pair.key}
                    onChange={(e) => handleMappingChange(index, "key", e.target.value)}
                    className="pair-key"
                  />
                  <Input
                    placeholder={t("VALUE")}
                    value={pair.value}
                    onChange={(e) => handleMappingChange(index, "value", e.target.value)}
                    className="pair-value"
                  />
                  {mappingPairs.length > 1 && (
                    <Button
                      icon={<DeleteRegular />}
                      appearance="subtle"
                      onClick={() => removeMappingPair(index)}
                    />
                  )}
                </div>
              ))}
            </div>
            {errors.mapping && (
              <div className="validation-error">
                {errors.mapping}
              </div>
            )}
            <Button
             className="workflow-form"
              icon={<Add20Filled />}
              appearance="primary"
              onClick={addMappingPair}
            >
              
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default WorkflowForm;
